import {useEffect} from 'react'
import {useNavigate, useParams, Link} from 'react-router'
import {useMutation} from '@tanstack/react-query'
import {tripService} from '@/services/tripService'
import SkeletonCards from '@/customComponents/editRideComponents/SkeletonCards.jsx'
import {plusIconLight} from '@img'

/**
 * `/rebook/:tripId` — the URL twin of `/return-booking/:parentTripId`, for rebooking a
 * past trip. It exists so the rebook action can be reached by LINK rather than only by
 * a button that already holds a trip in memory: the "Rebook" button in the emailed trip
 * summary points here (with the `?ref=` handoff param that RefAuthGate resolves
 * globally), so the passenger lands straight in the new trip's confirm flow.
 *
 * Same contract as ReturnBooking: POST on mount, redirect on success, and only render
 * something when it fails.
 */
function friendlyError(result) {
    if (!result) return 'Couldn\'t rebook this trip. Please try again.'
    if (result.statusCode === 404) return 'Trip not found — this booking may have been removed.'
    if (result.statusCode === 422) {
        const firstField = result.raw?.errors && Object.values(result.raw.errors)[0]
        const firstMsg = Array.isArray(firstField) ? firstField[0] : null
        return firstMsg || result.raw?.message || result.error || 'This trip cannot be rebooked.'
    }
    return result.error || 'Couldn\'t rebook this trip. Please try again.'
}

export default function Rebook() {
    const {tripId} = useParams()
    const navigate = useNavigate()

    const mutation = useMutation({
        mutationFn: () => tripService.rebookTrip(tripId),
        onSuccess: (result) => {
            if (!result.success) return
            const newId = result.data?.newTripId
            // A rebook lands as a Draft → confirm flow; an already-confirmed trip
            // (backend returning the existing one) → edit page. Same rule as ReturnBooking.
            const dest = result.data?.trip?.status === 'Draft' ? 'confirm' : 'edit'
            if (newId != null) navigate(`/rides/${newId}/${dest}`, {replace: true})
        },
    })

    useEffect(() => {
        mutation.mutate()
    }, [tripId])

    const result = mutation.data
    const hasError = mutation.isError || (result && !result.success)

    if (hasError) {
        return (
            <div className="py-5 pb-10">
                <div className="max-w-3xl mx-auto p-4 flex flex-col gap-4 items-center text-center">
                    <h2 className="font-bold text-xl mb-2!">Rebook</h2>
                    <p className="text-(--text-secondary) mb-0!">{friendlyError(result)}</p>
                    <div className="flex items-center gap-3 mt-4">
                        <button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending}
                                className="cursor-pointer px-6 py-2.5 rounded-full bg-(--text-primary) text-white font-medium disabled:opacity-50">
                            Try again
                        </button>
                        <Link to="/" replace className="cursor-pointer px-6 py-2.5 rounded-full bg-(--bg-grey) text-(--text-primary) font-medium">
                            Back to Home
                        </Link>
                        <Link to="/book-ride" className="cursor-pointer flex items-center gap-2 py-2.5 pr-5 pl-2 rounded-full bg-(--text-primary) text-white font-medium transition-colors hover:opacity-80">
                            <img alt="" src={plusIconLight} className="w-7"/>
                            Book New Ride
                        </Link>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="py-5 pb-10">
            <div className="max-w-3xl mx-auto p-4">
                <SkeletonCards/>
            </div>
        </div>
    )
}
